/*
################
## SelectList ##
################

* OPTIONS
    * profileModel: Model of data. Required.
    * optionsList[Array[Objects]]: Labels and values for the options. Required.
    * placeholder[String]: Text in background. Not required.

* EXAMPLE
<SelectList
    profileModel="userProfile.testSelectList"
    optionsList={[{label: 'Hola', value: 1}, {label: 'Chau', value: 2}]}
    isUpdated = {function(){
            console.log("updated");
        }
    {...this.props}
/>

*/
import 'react-select/dist/react-select.css';

import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Select from 'react-select';
import { Errors } from 'react-redux-form';
import { formatText } from 'core/i18n';
import { useEffect, useRef, useState } from 'react';
import { useEventListener } from 'hooks/useEventListener';
import { isChrome } from 'utilities/browser';
import focusActions from 'actions/focusActions';
import { actions as formActions } from 'react-redux-form';
import { recordError } from 'utilities/uTagging';
import scroll from 'utilities/scroll';

const SelectList = ({
    formActions,
    validations,
    onChange,
    userProfileForm,
    profileModel,
    searchable,
    focusAnimated,
    focusActions,
    userProfile,
    onBlur,
    autoCompleteField,
    style,
    onKeyDown,
    label,
    description,
    messagesError,
    optionsList,
    placeholder,
    isLoading,
    clearable,
    disabled,
    value,
    tooltip,
    classNameLabel,
    id,
    defaultValue,
    isUpdated,
    autofocus,
    profileModelLabel,
    onOpen,
    onClose,
}) => {
    const [trigger, setTrigger] = useState(false);
    const [classFocus, setClassFocus] = useState('');
    const [classError, setClassError] = useState('');
    const [autoBlur, setAutoBlur] = useState(false);
    const [offset, setOffset] = useState(0);
    const [parentElement, setParentElement] = useState('');
    const currentElement = useRef(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const modelProp = profileModel.split('.')[1];
        if (userProfileForm && userProfileForm && userProfileForm[modelProp]) {
            changeClassError(!userProfileForm[modelProp].valid);
        }
    }, [userProfileForm, profileModel]);

    useEffect(() => {
        const { investmentType } = userProfile;
        const modelProp = profileModel?.split('.')[1];
        if (autofocus) {
            currentElement.current.focus();
        }

        if (trigger) {
            if (userProfileForm[modelProp]?.valid) {
                if (isUpdated != null) {
                    isUpdated();
                }
            } else {
                recordError(`${modelProp}:${errorMessage}`, investmentType ? investmentType : '');

                changeClassError(true);
            }
            setTrigger(false);
        }
    }, [userProfile]);

    useEventListener('resize', () => {
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        setAutoBlur(width < 768);
    });

    const changeClassError = change => {
        const classError = change ? 'form__select--error valid-error' : '';
        setClassError(classError);
    };

    const changeClassFocus = change => {
        const classFocus = change ? 'form__select--focus' : '';
        setClassFocus(classFocus);
    };

    const handleOnChange = event => {
        const value = event != null ? event.value : '';
        const label = event != null ? event.label : '';
        formActions.change(profileModel, value);
        formActions.change(profileModelLabel, label);
        if (validations != null) {
            formActions.validate(profileModel, validations);
        }
        let animate = false;
        if (value !== '') {
            animate = true;
            formActions.validate(profileModel, { required: true });
        }
        if (!searchable) {
            changeClassFocus(animate);
        }
        if (onChange != null) {
            onChange(value);
        }
        changeClassError(false);
        setTrigger(true);

        const nextElement = currentElement.current.parentElement.parentElement.parentElement.nextSibling;
        if (nextElement) {
            setTimeout(() => {
                // setTimeout is necessary because BlockWrapper delays loading
                const element = nextElement.querySelectorAll('input, select');
                if (element.lengthh > 0) {
                    element[0].focus();
                }
            }, 100);
        }
    };

    const handleOnFocus = event => {
        let modelProp;

        if (focusAnimated) {
            onFocusAnimate(event);
        }

        if (typeof profileModel !== 'undefined') {
            modelProp = profileModel.split('.')[1];
            focusActions.selectFalseFocus(modelProp);
        }
        if (searchable) {
            changeClassFocus(true);
        }
    };

    const onFocusAnimate = event => {
        if (event) {
            const modelProp = profileModel.split('.')[1];
            const parentElement = document.getElementById(`id_wrapper_${modelProp}`);
            scroll.transition(parentElement);
            // set offset to state
            const bodyRect = document.body.getBoundingClientRect();
            const elementRect = parentElement.getBoundingClientRect();
            const innerHeight =
                window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
            const offset = elementRect.top - bodyRect.top - innerHeight / 2;
            setOffset(offset);
            setParentElement(parentElement);
        } else if (offset) {
            scroll.transition(parentElement);
            scroll.offset(offset);
        }
    };

    const handleOnBlur = () => {
        const modelProp = typeof profileModel !== 'undefined' ? profileModel.split('.')[1] : 'invalidProp';
        const value = typeof userProfile[modelProp] !== 'undefined' ? userProfile[modelProp] : '';

        if (onBlur) {
            onBlur(value);

            return;
        }

        if (validations != null) {
            formActions.validate(profileModel, validations);
        }
        if (value !== '') {
            formActions.validate(profileModel, { required: true });
        }

        setTrigger(true);
        changeClassFocus(false);
    };

    const renderErrors = ({ children }) => {
        setErrorMessage(children);

        return <p className="form__field_error">{children}</p>;
    };

    const isNotValid = () => {
        return false;
    };

    const modelProp = typeof profileModel !== 'undefined' ? profileModel.split('.')[1] : 'invalidProp';
    const className = `form__select ${classFocus} ${classError}`;
    let currentValue = userProfile[modelProp];
    if (value && !userProfile[modelProp]) {
        currentValue = value;
    }

    return (
        <div className={className} style={style} id={`id_wrapper_${modelProp}`} ref={currentElement}>
            {label ? (
                <label className={classNameLabel} htmlFor={id}>
                    {label}
                </label>
            ) : null}
            {description ? <span>{description}</span> : null}
            {tooltip}
            <Select.Creatable
                id={id}
                className={`input-select_${modelProp}`}
                value={currentValue}
                options={optionsList}
                autoBlur={autoBlur}
                onChange={handleOnChange}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                clearable={clearable}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                isLoading={isLoading}
                closeTimeOutMS={150}
                searchable={searchable}
                disabled={disabled}
                noResultsText={formatText('validation-noResultsFound')}
                isValidNewOption={isNotValid}
                defaultValue={defaultValue}
                inputProps={!autoCompleteField ? { autoComplete: isChrome() ? 'none' : 'off' } : undefined}
                onClose={onClose}
                onOpen={onOpen}
            />
            <Errors model={profileModel} messages={messagesError} component={renderErrors} />
        </div>
    );
};

SelectList.defaultProps = {
    messagesError: { required: formatText('validation-selectRequired') },
    label: '',
    onKeyDown: null,
    searchable: false,
    disabled: false,
    style: {},
    autofocus: false,
    classNameLabel: '',
    focusAnimated: false,
    description: null,
    defaultValue: null,
    autoCompleteField: true,
};

module.exports.constructor = SelectList;
module.exports.SelectList = connect(
    state => ({
        userProfile: state.userProfile,
        userProfileForm: state.userProfileForm,
    }),
    dispatch => ({
        focusActions: bindActionCreators(focusActions, dispatch),
        formActions: bindActionCreators(formActions, dispatch),
    })
)(SelectList);
